import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, AlertTriangle, Copy, Check, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { DESKTOP, PHONE, PHONE_INSTALLED, describeDevice } from "@/lib/device-context";
import { useTaal } from "@/contexts/TaalContext";

/**
 * Signing in with SPEAQ ID.
 *
 * Two situations, one component, because they are the same handshake seen from
 * two distances:
 *
 *   Another device holds your SPEAQ ID. Scan the code on this screen with it.
 *   This device holds your SPEAQ ID. There is nothing to scan with, so the six
 *   digits are the way, and on iOS with both apps installed they are the only
 *   way: two installed web apps cannot deep-link to each other.
 *
 * So the code is never hidden behind the QR, and the QR is never hidden behind
 * a toggle. Both are on screen, and the shortcut button opens the SPEAQ ID app
 * with the digits already filled in.
 *
 * The QR is black on white on a white tile, whatever the theme is doing. An
 * inverted QR looks better in the dark and iOS refuses to read it.
 */

const POLL_MS = 1500;

export default function SpeaqSignIn({ onSignedIn, onLinked, mode = "login", fetcher = fetch }) {
  const { t } = useTaal();
  const [phase, setPhase] = useState("idle");
  const [session, setSession] = useState(null);
  const [problem, setProblem] = useState(null);
  const [copied, setCopied] = useState(false);
  const running = useRef(false);
  // Read once, on the client. Which of the three situations this is decides
  // what the screen leads with, and on a phone with the app installed it
  // decides whether there is a working path at all.
  const [where, setWhere] = useState(DESKTOP);
  useEffect(() => {
    setWhere(
      describeDevice(
        typeof navigator === "undefined" ? undefined : navigator,
        typeof window === "undefined" ? undefined : window.matchMedia?.bind(window)
      )
    );
  }, []);

  const start = useCallback(async () => {
    setProblem(null);
    setPhase("starting");
    try {
      const res = await fetcher(`${API}/auth/speaq/start`, { method: "POST" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setProblem(body.detail || "SPEAQ ID could not be reached.");
        setPhase("problem");
        return;
      }
      setSession(await res.json());
      setPhase("waiting");
    } catch {
      setProblem("SPEAQ ID could not be reached. Your password still works.");
      setPhase("problem");
    }
  }, [fetcher]);

  // The poll owns its own interval and touches state only from inside the
  // callback. Setting state above the interval in this same effect is what
  // made an earlier Plexaris service poll exactly zero times under React's
  // strict mode: the cleanup cleared the interval before the first tick.
  useEffect(() => {
    if (phase !== "waiting" || !session?.sessionId) return undefined;
    running.current = true;

    const timer = setInterval(async () => {
      if (!running.current) return;
      let answer;
      try {
        const res = await fetcher(
          `${API}/auth/speaq/poll?sessionId=${encodeURIComponent(session.sessionId)}`
        );
        answer = await res.json();
      } catch {
        return; // A missed poll is not a failure. The next one is 1.5s away.
      }

      // Both of these mean the person approved. They differ only in whether
      // the DID already has an account here, and in link mode that difference
      // is the reason we are here: connecting a SPEAQ ID for the first time
      // answers "not_linked" by definition. Treating it as a refusal is what
      // made the connect flow show the sign-in error on 27 July, and the link
      // was never attempted at all.
      const approved = answer.status === "approved";
      const linkable = mode === "link" && (approved || answer.status === "not_linked");

      if (approved || linkable) {
        // The phase change is what tears the interval down; this flag only
        // stops a tick that is already in flight from doing it twice.
        running.current = false;
        setPhase("approved");
        if (mode === "link") onLinked?.(session.sessionId);
        else onSignedIn?.(answer);
        return;
      }
      if (answer.status === "not_linked") {
        running.current = false;
        setPhase("problem");
        setProblem(
          "That SPEAQ ID is not connected to an account on this machine yet. " +
            "Sign in with your password once and connect it in Settings."
        );
        return;
      }
      if (answer.status === "denied" || answer.status === "expired" || answer.status === "no_speaq_did") {
        running.current = false;
        setPhase("problem");
        setProblem(
          answer.status === "denied"
            ? t("De aanmelding is geweigerd in je SPEAQ ID-app.")
            : t("Die code geldt niet meer. Begin opnieuw.")
        );
      }
    }, POLL_MS);

    return () => {
      running.current = false;
      clearInterval(timer);
    };
  }, [phase, session, mode, onSignedIn, onLinked, fetcher, t]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(session.pairingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* the digits are on screen anyway */
    }
  };

  if (phase === "idle") {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full h-11"
        onClick={start}
        data-testid="speaq-start-btn"
      >
        <ShieldCheck className="h-4 w-4 mr-2" />
        {mode === "link" ? t("Verbind je SPEAQ ID") : t("Aanmelden met SPEAQ ID")}
      </Button>
    );
  }

  if (phase === "problem") {
    return (
      <div className="w-full text-center space-y-3" data-testid="speaq-problem">
        <AlertTriangle className="h-5 w-5 mx-auto text-corona" />
        <p className="text-xs text-muted-foreground">{problem}</p>
        <Button type="button" variant="outline" className="h-11" onClick={start}>
          {t("Opnieuw proberen")}
        </Button>
      </div>
    );
  }

  if (phase === "starting") {
    // Its own state on purpose. Falling through to the waiting screen would
    // say "waiting for approval" before there is anything to approve, and
    // there would be no code on screen to approve it with.
    return (
      <p className="w-full text-center text-xs text-muted-foreground" data-testid="speaq-starting">
        {t("SPEAQ ID om een code vragen...")}
      </p>
    );
  }

  if (phase === "approved") {
    return (
      <div className="w-full text-center space-y-2" data-testid="speaq-approved">
        <Check className="h-5 w-5 mx-auto text-ok" />
        <p className="text-xs text-muted-foreground">{t("Goedgekeurd.")}</p>
      </div>
    );
  }

  const onDesktop = where === DESKTOP;
  const qr = session?.qrPayload && (
    /* White tile, black modules, quiet zone. Not a place for theming: an
       inverted QR reads beautifully in a dark app and not at all on an
       iPhone camera. */
    <div className="bg-white p-4 rounded-lg inline-block" data-testid="speaq-qr">
      <QRCodeSVG value={session.qrPayload} size={180} marginSize={2} />
    </div>
  );

  return (
    <div className="w-full space-y-4" data-testid="speaq-waiting">
      {onDesktop ? (
        <div className="flex flex-col items-center gap-2">
          {qr}
          <p className="text-[11px] text-muted-foreground text-center">
            {t("Scan dit met de SPEAQ ID-app op je telefoon.")}
          </p>
        </div>
      ) : (
        /* Folded away on a phone. There is no second screen to scan it with,
           and two ways to sign in side by side is how the wrong one gets
           tried first. */
        <details className="text-center" data-testid="speaq-qr-folded">
          <summary className="text-[11px] text-muted-foreground cursor-pointer select-none">
            {t("Of toon de QR-code")}
          </summary>
          <div className="mt-3">{qr}</div>
        </details>
      )}

      {session?.pairingCode && (
        <div className="text-center space-y-2">
          <p className="text-[11px] text-muted-foreground">
            {onDesktop
              ? t("Of typ deze code in je SPEAQ ID-app.")
              : t("Open je SPEAQ ID-app en vul deze code in.")}
          </p>
          <div
            className="font-mono text-2xl tracking-[0.2em] text-foreground select-all"
            data-testid="speaq-pairing-code"
          >
            {session.pairingCode.slice(0, 3)} {session.pairingCode.slice(3)}
          </div>
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              variant="ghost"
              className="h-11 text-xs"
              onClick={copy}
              data-testid="speaq-copy-btn"
            >
              <Copy className="h-3 w-3 mr-1" />
              {copied ? t("Gekopieerd") : t("Code kopiëren")}
            </Button>
            {/* Only where a link can actually reach the SPEAQ app. From a web
                app installed on an iOS home screen it opens Safari instead,
                which looks broken and is the case Frank is in, so there the
                copy button above is the whole path. */}
            {where === PHONE && session.openAppUrl && (
              <Button
                type="button"
                variant="ghost"
                className="h-11 text-xs"
                onClick={() => window.open(session.openAppUrl, "_blank", "noopener")}
                data-testid="speaq-open-app-btn"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                {t("SPEAQ ID openen")}
              </Button>
            )}
          </div>
          {where === PHONE_INSTALLED && (
            <p className="text-[10px] text-muted-foreground" data-testid="speaq-installed-hint">
              {t("Kopieer de code, ga naar je SPEAQ ID-app en plak hem onder Code invoeren.")}
            </p>
          )}
        </div>
      )}

      <p className="text-[11px] text-center text-muted-foreground" data-testid="speaq-status">
        <span className="inline-block h-2 w-2 rounded-full bg-corona animate-pulse mr-2" />
        {t("Wachten op goedkeuring in je SPEAQ ID-app")}
      </p>
    </div>
  );
}
